import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import {NormalizedUserResult, silentSignIn, signInPopUp, updateUserCredentials} from '../../infrastructure/auth';
import {loadUser, updateUser} from '../user/actions';
import {setIsNewUserSession} from './actions';
import {ProfileState} from './types';

const initialState: ProfileState = {
  effectiveAccessProfile: {
    id: '',
    name: '',
    daysAhead: 0,
    daysPerWeek: 0,
    registrationDays: [],
    userId: '',
  },
  effectiveLocationProfile: {
    allowedLocationIds: [],
    disallowedLocationIds: [],
    id: '',
    name: '',
    userId: '',
    allowAll: true,
  },
  isLoading: false,
  parkingLocationProfile: {
    id: '',
    name: '',
    allowAll: false,
    allowedLocationIds: [],
    disallowedLocationIds: [],
    userId: '',
  },
  parkingQuotaProfile: {
    id: '',
    name: '',
    daysPerWeek: 1,
    userId: '',
  },
  isNewUserSession: false,
};

const setProfileState = (
  state: ProfileState,
  {accessProfiles, locationProfiles, parkingLocationProfiles, parkingQuotaProfiles}: NormalizedUserResult,
) => {
  const accessProfile = Object.values(accessProfiles)[0];
  const locationProfile = Object.values(locationProfiles)[0];
  const locationProfileParking = Object.values(parkingLocationProfiles)[0];
  const quotaProfileParking = Object.values(parkingQuotaProfiles)[0];
  state.effectiveAccessProfile = accessProfile;
  state.effectiveLocationProfile = locationProfile;
  state.parkingLocationProfile = locationProfileParking;
  state.parkingQuotaProfile = quotaProfileParking;
};

const slice = createSlice({
  name: 'profiles',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateUser.fulfilled, (state, action) => {
        setProfileState(state, action.payload);
      })
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        setProfileState(state, action.payload);
        state.isLoading = false;
      })
      .addCase(loadUser.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(setIsNewUserSession, (state, {payload}) => {
        state.isNewUserSession = payload;
      })
      .addMatcher(isAnyOf(silentSignIn.pending, signInPopUp.pending), (state) => {
        state.isLoading = true;
      })
      .addMatcher(
        isAnyOf(silentSignIn.fulfilled, signInPopUp.fulfilled, updateUserCredentials.fulfilled),
        (state, action) => {
          setProfileState(state, action.payload);
          state.isLoading = false;
        },
      )
      .addMatcher(isAnyOf(silentSignIn.rejected, signInPopUp.rejected), (state) => {
        state.isLoading = false;
      });
  },
});

export const {reducer: profilesReducer} = slice;
